import { useEffect, useState } from "react";

// A mark kept per account on this device, behind a bell row (autoSavedRow,
// profileNameRow on the phone; profileNameBell on the web): written by
// whoever finds the condition, forgotten when it is over, read by one hook
// that follows both. `parse` turns the stored text into the value (null:
// unreadable, as if absent). The host brings its storage (AsyncStorage on the
// phone, the guarded localStorage on the web) and how it names the key.

/** A key-value store, sync or async. */
export type OwnerMarkStorage = {
  getItem: (key: string) => string | null | Promise<string | null>;
  setItem: (key: string, value: string) => void | Promise<void>;
  removeItem: (key: string) => void | Promise<void>;
};

export function createOwnerMark<T>(opts: {
  base: string;
  parse: (raw: string) => T | null;
  serialize: (value: T) => string;
  /** For the warnings: what the mark stands for. */
  label: string;
  storage: OwnerMarkStorage;
  /** The storage key of one account's mark. */
  keyOf: (owner: string) => string;
}) {
  const listeners = new Set<() => void>();
  const key = (owner: string) => opts.keyOf(owner);
  const told = () => {
    for (const fn of [...listeners]) fn();
  };

  async function load(owner: string): Promise<T | null> {
    try {
      const raw = await opts.storage.getItem(key(owner));
      return raw === null ? null : opts.parse(raw);
    } catch {
      return null;
    }
  }

  async function record(owner: string, value: T): Promise<void> {
    try {
      await opts.storage.setItem(key(owner), opts.serialize(value));
    } catch (err) {
      console.warn(`[bell] ${opts.label} not recorded`, err);
    }
    told();
  }

  async function forget(owner: string): Promise<void> {
    try {
      await opts.storage.removeItem(key(owner));
    } catch (err) {
      console.warn(`[bell] ${opts.label} not forgotten`, err);
    }
    told();
  }

  /** The mark: undefined until read, then the value or null. */
  function useMark(owner: string | null): T | null | undefined {
    const [mark, setMark] = useState<T | null | undefined>(undefined);
    useEffect(() => {
      if (owner === null) return undefined;
      let live = true;
      const read = () => {
        void load(owner).then((m) => {
          if (live) setMark(m);
        });
      };
      read();
      listeners.add(read);
      return () => {
        live = false;
        listeners.delete(read);
      };
    }, [owner]);
    return mark;
  }

  return { record, forget, useMark };
}
